import { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { useAppZoom } from "../../contexts/AppZoomContext";
import { FINES_MATERIAL_KEY } from "../../containers/activity/utils/buildMaterialsWithFines";
import { formatPercentage } from "../../shared/utils/utils";

const BAR_HEIGHT = 24;
const BAR_GAP_RATIO = 0.5;
const BAR_SLOT_HEIGHT = BAR_HEIGHT * (1 + BAR_GAP_RATIO);
const GRID_TOP = 10;
const GRID_BOTTOM = 8;
// Extra room so containLabel x-axis ticks don't squeeze barWidth.
const X_AXIS_LABEL_SPACE = 22;
const CARD_HEIGHT = 380;
const MAX_LABEL_LENGTH = 22;
const GRID_LINE_COLOR = "#E5E7EB";
const AXIS_LABEL_COLOR = "#9CA3AF";

const truncateLabel = (label, maxLength = MAX_LABEL_LENGTH) => {
  if (!label) return "";
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1).trimEnd()}…`;
};

/**
 * Modern horizontal bar chart used to replace pie charts when the number of
 * categories is too high to stay readable as a donut (30-50+ materials).
 * Bars keep a fixed thickness regardless of category count; once the
 * content overflows the fixed card height, the chart area itself scrolls
 * vertically instead of shrinking the bars.
 * Categories are always sorted by descending value.
 */
export const HorizontalDistributionBarChart = ({
  dataChart,
  labelChart,
  colorChart,
  // Untranslated material keys, parallel to labelChart. Only needed when
  // onSelectCategory is used (click-to-select in a paired "zoom" panel).
  valueChart,
  materialTotal,
  chartTotal,
  totalLabel,
  height = CARD_HEIGHT,
  // Optional second dataset (e.g. sorting errors only, computed on the same
  // categories) shown via a toggle instead of a second, near-duplicate card.
  secondaryDataset,
  primaryToggleLabel,
  secondaryToggleLabel,
  // Raw value (from valueChart) of the currently selected category, used to
  // highlight the matching bar.
  selectedValue,
  onSelectCategory,
}) => {
  const { t } = useTranslation();
  const { zoomLevel } = useAppZoom();
  const zoomFactor = zoomLevel / 100;
  const isDisplayZoomed = zoomFactor > 0 && zoomFactor !== 1;
  const [datasetMode, setDatasetMode] = useState("primary");
  const chartDomRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const showDatasetToggle = Boolean(secondaryDataset);
  const activeDataset =
    showDatasetToggle && datasetMode === "secondary"
      ? secondaryDataset
      : {
          dataChart,
          labelChart,
          colorChart,
          valueChart,
          materialTotal,
          chartTotal,
          totalLabel,
        };

  const percentageDenominator =
    activeDataset.chartTotal != null
      ? activeDataset.chartTotal
      : activeDataset.materialTotal;

  const sortedEntries = useMemo(() => {
    const entries = (activeDataset.dataChart || []).map((value, index) => ({
      value,
      categoryValue: activeDataset.valueChart?.[index],
      label: activeDataset.labelChart?.[index] ?? "",
      color: activeDataset.colorChart?.[index],
      percentage: Number(formatPercentage(value, percentageDenominator)),
    }));

    return [...entries].sort((a, b) => b.value - a.value);
  }, [
    activeDataset.dataChart,
    activeDataset.labelChart,
    activeDataset.colorChart,
    activeDataset.valueChart,
    percentageDenominator,
  ]);

  // Fixed per-bar slot (+ grid chrome) so bar thickness stays identical
  // across dataset modes even when category counts differ.
  const chartContentHeight =
    sortedEntries.length * BAR_SLOT_HEIGHT +
    GRID_TOP +
    GRID_BOTTOM +
    X_AXIS_LABEL_SPACE;

  useEffect(() => {
    if (!chartDomRef.current) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartDomRef.current);
    }
    const chart = chartInstanceRef.current;

    // Category axis is rendered top-to-bottom in array order, so reversing
    // here keeps the highest values at the top.
    const orderedEntries = [...sortedEntries].reverse();

    const option = {
      grid: {
        left: 12,
        right: 49,
        top: GRID_TOP,
        bottom: GRID_BOTTOM,
        containLabel: true,
      },
      tooltip: onSelectCategory
        ? {
            trigger: "item",
            formatter: (params) => {
              const entry = orderedEntries[params.dataIndex];
              if (entry?.categoryValue === FINES_MATERIAL_KEY) {
                return null;
              }
              return t("Click to view objects");
            },
            position: (pos, params) => {
              const entry = orderedEntries[params.dataIndex];
              if (entry?.categoryValue === FINES_MATERIAL_KEY) {
                return [-9999, -9999];
              }
              return pos;
            },
          }
        : { show: false },
      xAxis: {
        type: "value",
        min: 0,
        max: 100,
        interval: 20,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: AXIS_LABEL_COLOR,
          fontSize: 11,
          formatter: (value) => `${value}%`,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: GRID_LINE_COLOR,
            width: 1,
          },
        },
      },
      yAxis: {
        type: "category",
        data: orderedEntries.map((entry) => entry.label),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: "#5E5E5E",
          fontSize: 12,
          formatter: (label) => truncateLabel(label),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: GRID_LINE_COLOR,
            width: 1,
          },
        },
      },
      series: [
        {
          type: "bar",
          data: orderedEntries.map((entry) => {
            const isFines = entry.categoryValue === FINES_MATERIAL_KEY;
            const isDimmed =
              selectedValue != null &&
              entry.categoryValue !== selectedValue &&
              !isFines;
            return {
              value: entry.percentage,
              cursor: onSelectCategory && !isFines ? "pointer" : "default",
              itemStyle: {
                color: entry.color,
                borderRadius: [0, 4, 4, 0],
                opacity: isDimmed ? 0.4 : 1,
              },
              emphasis: isFines
                ? { disabled: true }
                : {
                    itemStyle: {
                      opacity: 1,
                      shadowBlur: 8,
                      shadowColor: "rgba(0, 0, 0, 0.18)",
                    },
                  },
            };
          }),
          barWidth: BAR_HEIGHT,
          barCategoryGap: `${BAR_GAP_RATIO * 100}%`,
          z: 2,
          label: {
            show: true,
            position: "right",
            formatter: (params) => `${params.value}%`,
            color: "#222222",
            fontSize: 12,
            fontWeight: 600,
          },
        },
      ],
    };

    chart.setOption(option, true);
    chart.resize();

    chart.off("click");
    if (onSelectCategory) {
      chart.on("click", (params) => {
        const entry = orderedEntries[params.dataIndex];
        if (
          entry?.categoryValue != null &&
          entry.categoryValue !== FINES_MATERIAL_KEY
        ) {
          onSelectCategory(entry.categoryValue);
        }
      });
    }

    const resizeFn = () => chart.resize();
    window.addEventListener("resize", resizeFn);
    return () => window.removeEventListener("resize", resizeFn);
  }, [
    sortedEntries,
    chartContentHeight,
    selectedValue,
    onSelectCategory,
    t,
    zoomLevel,
  ]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // Cancel parent #root CSS zoom for ECharts hit-testing, then scale back
  // visually (same workaround as LineChart for displayZoom offset).
  const zoomCompensationStyle = isDisplayZoomed
    ? {
        width: `${100 / zoomFactor}%`,
        height: `${100 / zoomFactor}%`,
        zoom: String(1 / zoomFactor),
        transform: `scale(${zoomFactor})`,
        transformOrigin: "0 0",
      }
    : {
        width: "100%",
        height: "100%",
      };

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="flex items-center justify-between gap-2 pb-3 min-w-0 flex-wrap">
        <h3 className="text-xl font-bold text-[#222222] m-0 leading-tight min-w-0">
          {activeDataset.materialTotal} {activeDataset.totalLabel}
        </h3>
        {showDatasetToggle && (
          <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1 h-10 shrink-0">
            <button
              type="button"
              onClick={() => setDatasetMode("primary")}
              className={`rounded-full h-full px-3.5 text-[14px] font-medium whitespace-nowrap transition-colors ${
                datasetMode === "primary"
                  ? "bg-white text-[#222222] shadow-sm"
                  : "text-[#9CA3AF] hover:text-[#5E5E5E]"
              }`}
            >
              {primaryToggleLabel ?? t("all_materials")}
            </button>
            <button
              type="button"
              onClick={() => setDatasetMode("secondary")}
              className={`rounded-full h-full px-3.5 text-[14px] font-medium whitespace-nowrap transition-colors ${
                datasetMode === "secondary"
                  ? "bg-white text-[#222222] shadow-sm"
                  : "text-[#9CA3AF] hover:text-[#5E5E5E]"
              }`}
            >
              {secondaryToggleLabel ?? t("sorting_errors_only")}
            </button>
          </div>
        )}
      </div>
      <div
        className="flex-1 overflow-y-auto px-1.5 pb-2 min-h-0"
        style={{ height }}
      >
        <div
          style={{
            width: "100%",
            height: chartContentHeight,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={zoomCompensationStyle}>
            <div ref={chartDomRef} style={{ width: "100%", height: "100%" }} />
          </div>
        </div>
      </div>
    </div>
  );
};
